/**
 * Entity kind registry — closed enum of LLM-wiki entity kinds, plus per-kind
 * role / embedding-slot / edge-kind enums.
 *
 * Design context: docs/plans/2026-05-26-llm-wiki-entities-prototype-design.md
 *
 * Substrate invariant #5: the DB stores `entities.kind`, `entity_threads.role`,
 * `entity_edges.kind`, and `entity_embeddings.slot` as open `varchar` columns
 * (no CHECK constraints). Zod is the source of truth for which values are
 * valid per kind. New kinds are added here first; the registry in `index.ts`
 * uses `satisfies Record<EntityKind, …>` to make a missing payload schema a
 * compile error.
 */

import { z } from 'zod';

// ── Kinds ────────────────────────────────────────────────────────────

export const entityKindValues = ['person', 'foot_gun', 'decision'] as const;
export const entityKindSchema = z.enum(entityKindValues);
export type EntityKind = z.infer<typeof entityKindSchema>;

// ── Per-kind role enums (entity_threads.role) ────────────────────────
//
// Role values are open strings in the DB; Zod enumerates the valid set
// per kind. The substrate writer is the only place that validates a
// (kind, role) pair against the matching schema below.

export const personRoleSchema = z.enum([
  'authored',
  'commented',
  'edited',
  'mentioned',
]);
export type PersonRole = z.infer<typeof personRoleSchema>;

export const footGunRoleSchema = z.enum(['observed_in', 'reaffirmed_in']);
export type FootGunRole = z.infer<typeof footGunRoleSchema>;

export const decisionRoleSchema = z.enum(['derived_from', 'reaffirmed_in']);
export type DecisionRole = z.infer<typeof decisionRoleSchema>;

// ── Per-kind embedding-slot enums (entity_embeddings.slot) ───────────
//
// Person has no embedding slots — Person↔User mirroring doesn't need
// vector retrieval (D10). Zod rejects an empty `z.enum([])`, so we
// express "no slots" as a typed empty tuple at the type level rather
// than as a runtime schema.

export const personSlots = [] as const;
export type PersonSlot = (typeof personSlots)[number];

export const footGunSlotValues = ['primary'] as const;
export const footGunSlotSchema = z.enum(footGunSlotValues);
export type FootGunSlot = z.infer<typeof footGunSlotSchema>;

export const decisionSlotValues = ['statement', 'rationale'] as const;
export const decisionSlotSchema = z.enum(decisionSlotValues);
export type DecisionSlot = z.infer<typeof decisionSlotSchema>;

// ── Decision lifecycle (entities.lifecycle — a STORED column) ─────────
//
// `lifecycle` is a stored column on `lore.entities` (reserved in migration
// 20260601120000), NOT derived from edge state. The extractor WRITES it:
// 'active' on create, and it flips the predecessor to 'superseded' when it
// links a `supersedes` edge (north-star §8 pin 3). `visible_decisions` SELECTs
// the column; the decision search descriptor scores a NULL lifecycle at the
// conservative 0.6 (the superseded factor). The type lives here so producers
// (extractor) and consumers (search) agree on the closed set of values.

export const decisionLifecycleValues = ['active', 'superseded'] as const;
export const decisionLifecycleSchema = z.enum(decisionLifecycleValues);
export type DecisionLifecycle = z.infer<typeof decisionLifecycleSchema>;

// ── Edge-kind enum (entity_edges.kind) ───────────────────────────────
//
// Shared across kinds in v1 — `supports`, `supersedes`, and the
// attribution kinds (`decided_by`, `involved_in`) are the only relations
// the launch kinds need. Per-kind edge-kind enums can be split out later
// if the set diverges.

export const entityEdgeKindValues = [
  'supports',
  'supersedes',
  'decided_by',
  'involved_in',
] as const;
export const entityEdgeKindSchema = z.enum(entityEdgeKindValues);
export type EntityEdgeKind = z.infer<typeof entityEdgeKindSchema>;

// ── Attribution edge-kind subset (decision → person) ─────────────────
//
// `decided_by` and `involved_in` are the subset of `entityEdgeKindValues`
// that attribute a decision to the people behind it. Edge direction is
// decision → person (`from` = decision entity, `to` = person entity);
// nothing in the DB enforces this, the single writer (substrate) does.
// Later tasks gate on this narrower enum rather than the full edge-kind
// set so a future non-attribution edge kind can't accidentally qualify.

export const attributionEdgeKindValues = ['decided_by', 'involved_in'] as const;
export const attributionEdgeKindSchema = z.enum(attributionEdgeKindValues);
export type AttributionEdgeKind = z.infer<typeof attributionEdgeKindSchema>;

// ── Edge payload schemas (entity_edges.payload) ──────────────────────
//
// Edge payloads are per-edge-kind, not per-entity-kind, so they live
// next to `entityEdgeKindValues` rather than in the per-entity-kind
// payload registry in `index.ts`. `authority`/`status` follow the same
// I7 constraint as entity payloads: a required literal today, promotion
// to a union is a later additive change.

export const supersedesEdgePayloadSchema = z.object({
  status: z.literal('extracted'),
  evidence: z
    .object({
      threadId: z.string(),
      threadDecisionId: z.string().optional(),
    })
    .optional(),
});
export type SupersedesEdgePayload = z.infer<typeof supersedesEdgePayloadSchema>;

// Shared payload for both attribution edge kinds (`decided_by`,
// `involved_in`). `status: 'extracted'` mirrors the `supersedes`
// contract above so a future confirmed tier is a column promotion, not
// a reshape.

export const attributionEdgePayloadSchema = z.object({
  status: z.literal('extracted'),
  sourceThreadIds: z.array(z.string()).nonempty(),
});
export type AttributionEdgePayload = z.infer<typeof attributionEdgePayloadSchema>;
